import type { components } from "@certn/api/generated-schemas/client-portal";

export type {
  EnrollmentDetailCheck,
  MonitoringRunChange,
  MonitoringAlertNotification,
  MonitoringActivityLogRun,
  MonitoringRunDetail,
} from "@certn/monitoring";

export type EligibleMonitoringCheck =
  components["schemas"]["EligibleMonitoringCheckSummary"];
export type EligibleMonitoringApplicant =
  components["schemas"]["EligibleMonitoringApplicantList"];
export type EligibleApplicantsResponse =
  components["schemas"]["PaginatedEligibleMonitoringApplicantListList"];
export type MonitoringApplicantDetail =
  components["schemas"]["MonitoringApplicantDetail"];
export type MonitoringEnrollmentResponse =
  components["schemas"]["MonitoringEnrollmentResponse"];
export type DashboardApplicant =
  components["schemas"]["MonitoringDashboardApplicantList"];
export type DashboardApplicantsResponse =
  components["schemas"]["PaginatedMonitoringDashboardApplicantListList"];
export type MonitoredCheckTypeSummary =
  components["schemas"]["MonitoredCheckTypeSummary"];
export type MonitoringActivityLogResponse =
  components["schemas"]["PaginatedMonitoringActivityLogRunList"];
export type MonitoringDashboardSummary =
  components["schemas"]["MonitoringDashboardSummary"];
export type MonitoringBulkActionRequest =
  components["schemas"]["BulkActionInput"];
export type MonitoringBulkActionResponse =
  components["schemas"]["MonitoringBulkActionResponse"];
export type BulkMonitoringActionType =
  components["schemas"]["BulkActionInputActionEnum"];
export type MonitoringEnrollmentDemoResponse =
  components["schemas"]["MonitoringEnrollmentDemoResponseRead"];

export interface ApplicantNameFields {
  id: string;
  latest_first_name: string | null;
  latest_last_name: string | null;
  latest_email?: string | null;
}

export function getApplicantDisplayName(a: ApplicantNameFields): string {
  return (
    [a.latest_first_name, a.latest_last_name].filter(Boolean).join(" ") ||
    a.latest_email ||
    a.id
  );
}

function uniqueInOrder(values: string[]): string[] {
  return Array.from(new Set(values));
}

/** Distinct check names across an applicant's eligible monitoring checks. */
export function getApplicantCheckNames(
  a: EligibleMonitoringApplicant
): string[] {
  return uniqueInOrder(a.eligible_monitoring_checks.map((c) => c.name));
}

/** Distinct customer-group names across an applicant's eligible checks. */
export function getApplicantGroupNames(
  a: EligibleMonitoringApplicant
): string[] {
  return uniqueInOrder(
    a.eligible_monitoring_checks
      .map((c) => c.customer_group_name)
      .filter((name): name is string => Boolean(name))
  );
}

export function toggleSetItem<T>(set: Set<T>, item: T): Set<T> {
  const next = new Set(set);
  if (next.has(item)) {
    next.delete(item);
  } else {
    next.add(item);
  }
  return next;
}
